import { Avatar } from "@moe/oss/ui/avatar";
import { useHorizontalScroll } from "@moe/priv/hooks/use-horizontal-scroll";
import { cn } from "@moe/priv/utils";
import { useEffect, useRef, useState } from "react";

interface AvatarGroupProps {
  avatars: Array<{
    url: string;
    onClick?: () => void;
  }>;
  maxWidth?: string;
  maxAvatars?: number;
  avatarClassName?: string;
  className?: string;
  onGroupClick?: () => void;
}

export function AvatarGroup({
  avatars,
  maxWidth = "14rem",
  avatarClassName,
  maxAvatars = 99,
  className,
  onGroupClick
}: AvatarGroupProps) {
  const scrollProps = useHorizontalScroll(0.8);
  const scrollRef = useRef<HTMLButtonElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);
  const sliced = avatars.slice(0, maxAvatars);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    const checkOverflow = () => {
      setShowLeftFade(element.scrollLeft > 0);
      setShowRightFade(element.scrollLeft + element.clientWidth < element.scrollWidth);
    };

    checkOverflow();
    element.addEventListener("scroll", checkOverflow);
    window.addEventListener("resize", checkOverflow);

    return () => {
      element.removeEventListener("scroll", checkOverflow);
      window.removeEventListener("resize", checkOverflow);
    };
  }, [avatars.length]);

  const baseAvatarClasses = "cursor-pointer rounded-full  transition-all duration-150 ease-out";

  const multiAvatarClasses = "ring-background ring-4";

  if (sliced.length === 0) return <Avatar src="" className={avatarClassName} />;

  if (sliced.length === 1) {
    const avatar = sliced[0];
    return (
      <button className="shrink-0 focus:outline-none" onClick={avatar.onClick || onGroupClick}>
        <Avatar src={avatar.url} className={cn(baseAvatarClasses, avatarClassName)} />
      </button>
    );
  }

  return (
    <div className="relative" style={{ maxWidth }}>
      <div
        className={`from-background pointer-events-none absolute left-0 top-0 z-10 h-full w-4 bg-gradient-to-r to-transparent md:w-7 ${
          showLeftFade ? "opacity-100" : "opacity-0"
        } transition duration-300 ease-out`}
      />
      <div
        className={`from-background pointer-events-none absolute right-0 top-0 z-10 h-full w-4 bg-gradient-to-l to-transparent md:w-7 ${
          showRightFade ? "opacity-100" : "opacity-0"
        } transition duration-300 ease-out`}
      />
      <button
        ref={scrollRef}
        {...scrollProps}
        className={cn(
          "scroll-hidden relative flex shrink-0 -space-x-3 overflow-x-auto scroll-smooth py-1.5 focus:outline-none",
          className
        )}
        onClick={onGroupClick}
      >
        {sliced.map((avatar, index) => (
          <Avatar
            key={index}
            src={avatar.url}
            className={cn(baseAvatarClasses, multiAvatarClasses, avatarClassName)}
            onClick={(e) => {
              if (avatar.onClick) {
                e.stopPropagation();
                avatar.onClick();
              }
            }}
          />
        ))}
      </button>
    </div>
  );
}
